import React, { useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import SectionMessage from '@atlaskit/section-message/message';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const i18n = defineMessages({
	body: {
		id: 'bulk-permissions.agent-access-warning.body',
		defaultMessage:
			'Adding an agent to all spaces makes all open content in the spaces accessible to anyone who can use the agent, even if they wouldn\u2019t have content access otherwise.',
		description:
			'Warning shown in bulk permissions when an AI agent is staged in the user picker, explaining the access implications',
	},
	dismiss: {
		id: 'bulk-permissions.agent-access-warning.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the dismiss button on the agent access warning',
	},
});

const STORAGE_SURFACE_KEY = 'bulk-permissions';
const AGENT_WARNING_DISMISSED_KEY =
	PERSISTED_KEYS_ON_SERVER.PERSISTED_AGENT_ACCESS_WARNING_DISMISSED;

type AgentAccessWarningProps = {
	stagedPrincipals: ReadonlyArray<{ appType?: string | null }>;
};

export const AgentAccessWarning = ({
	stagedPrincipals,
}: AgentAccessWarningProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isDismissed, setIsDismissed] = useState(() => {
		try {
			const dismissed = JSON.parse(localStorage.getItem(AGENT_WARNING_DISMISSED_KEY));
			return STORAGE_SURFACE_KEY in (dismissed ?? {});
		} catch {
			return false;
		}
	});

	const hasAgent = stagedPrincipals.some((p) => p?.appType === 'agent');

	useEffect(() => {
		if (!hasAgent || isDismissed || !fg('aa-296_fe_rovo_agents_can_take_actions')) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'agentAccessWarningBannerScreen',
				action: 'viewed',
				actionSubject: 'screen',
				source: 'bulkPermissions',
			},
		}).fire();
	}, [hasAgent, isDismissed, createAnalyticsEvent]);

	const handleDismiss = useCallback(() => {
		try {
			const existing = JSON.parse(localStorage.getItem(AGENT_WARNING_DISMISSED_KEY)) ?? {};
			localStorage.setItem(
				AGENT_WARNING_DISMISSED_KEY,
				JSON.stringify({
					...existing,
					[STORAGE_SURFACE_KEY]: 1,
				}),
			);
			setIsDismissed(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'agentAccessWarningDismissClick',
					source: 'bulkPermissions',
				},
			}).fire();
		} catch {
			return;
		}
	}, [createAnalyticsEvent]);

	if (!hasAgent || isDismissed || !fg('aa-296_fe_rovo_agents_can_take_actions')) {
		return null;
	}

	return (
		<Box xcss={styles.container}>
			<SectionMessage appearance="warning" testId="agent-access-warning-bulk-permissions">
				<Box xcss={styles.body}>{formatMessage(i18n.body)}</Box>
			</SectionMessage>
			<Box xcss={styles.dismissButton}>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(i18n.dismiss)}
					appearance="subtle"
					onClick={handleDismiss}
					testId="agent-access-warning-dismiss-bulk-permissions"
				/>
			</Box>
		</Box>
	);
};

const styles = cssMap({
	container: {
		position: 'relative',
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
	body: {
		paddingRight: token('space.400'),
	},
	dismissButton: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});
